import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Hand, LogIn, UserPlus, LogOut, User as UserIcon } from 'lucide-react';
import '../../styles/staffing-theme.css';
import EarlyAccessModal from '../EarlyAccessModal';

/**
 * TopNav — Global persistent navigation (Elite Dark).
 *
 * Phase 15 (AUTHENTICATION_STATE, 2/4):
 *   Reads mock auth from localStorage key `sv_user` (written by
 *   <EarlyAccessModal> on successful submit). If present → renders the
 *   user's email + a Log Out button. If absent → renders the original
 *   Log In / Sign Up CTAs. Log Out clears `sv_user` and redirects to `/`.
 *   No real auth yet — full auth ships with Stripe.
 *
 * Phase 14 additions (kept):
 *   • Log In / Sign Up open <EarlyAccessModal> with a source hint. Lead
 *     is POSTed to /api/leads.
 *   • Tab active-state rules updated for the `/signvoice*` path.
 *
 * Rendered once at the App root, self-suppresses only on
 * /talent-connect/candidate* (iPhone-first full-screen).
 */

const AUTH_KEY = 'sv_user'; // Phase 15 — standardized mock-auth storage key.

function readAuthUser() {
  try {
    const raw = typeof localStorage !== 'undefined' && localStorage.getItem(AUTH_KEY);
    if (!raw) return null;
    const u = JSON.parse(raw);
    if (u && typeof u.email === 'string' && u.email) return u;
    return null;
  } catch { return null; }
}

const NAV_ITEMS = [
  {
    label: 'SignVoice',
    path: '/signvoice',
    testid: 'tc-nav-signvoice',
    // Active on signvoice + legacy consumer paths + POC diagnostics. NOT
    // active on the marketing landing (`/`) — that's intentional so neither
    // tab dominates the entry point.
    match: (p) =>
      p === '/signvoice' ||
      p.startsWith('/signvoice/') ||
      p === '/consumer' ||
      p.startsWith('/consumer/') ||
      p === '/poc',
  },
  {
    label: 'TalentConnect',
    path: '/talent-connect',
    testid: 'tc-nav-talentconnect',
    match: (p) => p === '/talent-connect' || p.startsWith('/talent-connect/'),
  },
];

function shouldRender(pathname) {
  if (
    pathname === '/talent-connect/candidate' ||
    pathname.startsWith('/talent-connect/candidate/')
  ) {
    return false;
  }
  return true;
}

export default function TopNav() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [authModal, setAuthModal] = useState({ open: false, source: 'signup' });
  const [authUser, setAuthUser] = useState(readAuthUser);

  // Phase 15 — subscribe to auth changes fired by <EarlyAccessModal> (same tab)
  // and the native `storage` event (cross-tab).
  useEffect(() => {
    const reread = () => setAuthUser(readAuthUser());
    window.addEventListener('storage', reread);
    window.addEventListener('sv-auth:changed', reread);
    return () => {
      window.removeEventListener('storage', reread);
      window.removeEventListener('sv-auth:changed', reread);
    };
  }, []);

  const handleLogout = useCallback(() => {
    try {
      localStorage.removeItem(AUTH_KEY);
      window.dispatchEvent(new Event('sv-auth:changed'));
    } catch { /* noop */ }
    setOpen(false);
    navigate('/');
  }, [navigate]);

  if (!shouldRender(pathname)) return null;

  const openAuth = (source) => {
    setAuthModal({ open: true, source });
    setOpen(false);
  };
  const closeAuth = () => setAuthModal((s) => ({ ...s, open: false }));

  const isLoggedIn = !!authUser;
  // Truncated email for the compact nav pill — full email in the tooltip.
  const emailShort = isLoggedIn
    ? (authUser.email.length > 22 ? `${authUser.email.slice(0, 20)}…` : authUser.email)
    : '';

  return (
    <>
      <nav
        className="tc-topnav"
        data-testid="tc-topnav"
        role="navigation"
        aria-label="Primary navigation"
      >
        <Link
          to="/"
          className="tc-topnav-brand"
          data-testid="tc-brand"
          onClick={() => setOpen(false)}
          aria-label="SignVoice home"
        >
          <span className="tc-topnav-mark" aria-hidden="true">
            <Hand className="w-3.5 h-3.5" strokeWidth={2.5} />
          </span>
          <span className="tc-topnav-brand-text">SIGNVOICE</span>
        </Link>

        <ul
          className={`tc-topnav-links ${open ? 'tc-topnav-links--open' : ''}`}
          data-testid="tc-nav-list"
        >
          {NAV_ITEMS.map((item) => {
            const active = item.match(pathname);
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`tc-topnav-link ${active ? 'tc-topnav-link--active' : ''}`}
                  data-testid={item.testid}
                  aria-current={active ? 'page' : undefined}
                  onClick={() => setOpen(false)}
                >
                  {item.label}
                  {active && <span className="tc-topnav-indicator" aria-hidden="true" />}
                </Link>
              </li>
            );
          })}

          {/* Auth CTAs — inlined inside the <ul> so the mobile drawer picks
              them up automatically. On desktop we hide these via CSS and
              render the desktop copies inside .tc-topnav-right below.
              Phase 15 — swap to email + Log Out when `sv_user` is present. */}
          <li className="tc-topnav-drawer-auth">
            {isLoggedIn ? (
              <>
                <span
                  className="tc-topnav-auth-btn tc-topnav-auth-btn--ghost"
                  data-testid="tc-nav-user-email-mobile"
                  title={authUser.email}
                >
                  <UserIcon size={14} strokeWidth={2.2} />
                  {emailShort}
                </span>
                <button
                  type="button"
                  className="tc-topnav-auth-btn tc-topnav-auth-btn--primary"
                  onClick={handleLogout}
                  data-testid="tc-nav-logout-mobile"
                >
                  <LogOut size={14} strokeWidth={2.2} />
                  Log Out
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="tc-topnav-auth-btn tc-topnav-auth-btn--ghost"
                  onClick={() => openAuth('login')}
                  data-testid="tc-nav-login-mobile"
                >
                  <LogIn size={14} strokeWidth={2.2} />
                  Log In
                </button>
                <button
                  type="button"
                  className="tc-topnav-auth-btn tc-topnav-auth-btn--primary"
                  onClick={() => openAuth('signup')}
                  data-testid="tc-nav-signup-mobile"
                >
                  <UserPlus size={14} strokeWidth={2.2} />
                  Sign Up
                </button>
              </>
            )}
          </li>

          <li className="tc-topnav-drawer-beta" aria-hidden="true">
            <span className="tc-topnav-beta">Beta</span>
          </li>
        </ul>

        <div className="tc-topnav-right">
          {isLoggedIn ? (
            <>
              <span
                className="tc-topnav-auth-btn tc-topnav-auth-btn--ghost tc-topnav-auth-btn--desktop"
                data-testid="tc-nav-user-email"
                title={authUser.email}
              >
                <UserIcon size={14} strokeWidth={2.2} />
                <span>{emailShort}</span>
              </span>
              <button
                type="button"
                className="tc-topnav-auth-btn tc-topnav-auth-btn--primary tc-topnav-auth-btn--desktop"
                onClick={handleLogout}
                data-testid="tc-nav-logout"
              >
                <LogOut size={14} strokeWidth={2.2} />
                <span>Log Out</span>
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="tc-topnav-auth-btn tc-topnav-auth-btn--ghost tc-topnav-auth-btn--desktop"
                onClick={() => openAuth('login')}
                data-testid="tc-nav-login"
              >
                <LogIn size={14} strokeWidth={2.2} />
                <span>Log In</span>
              </button>
              <button
                type="button"
                className="tc-topnav-auth-btn tc-topnav-auth-btn--primary tc-topnav-auth-btn--desktop"
                onClick={() => openAuth('signup')}
                data-testid="tc-nav-signup"
              >
                <UserPlus size={14} strokeWidth={2.2} />
                <span>Sign Up</span>
              </button>
            </>
          )}
          <span
            className="tc-topnav-beta tc-topnav-beta--desktop"
            data-testid="tc-beta-badge"
          >
            Beta
          </span>
          <button
            type="button"
            className="tc-topnav-hamburger"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            data-testid="tc-nav-hamburger"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </nav>
      <div className="tc-topnav-spacer" aria-hidden="true" />

      <EarlyAccessModal
        isOpen={authModal.open}
        source={authModal.source}
        referrerPath={pathname}
        onClose={closeAuth}
      />
    </>
  );
}
